import React, { useEffect, useRef } from "react";
import Head from 'next/head'
import CartItem from '@/components/Cart/CartItem'
import Button from '@/components/Button'
import Link from 'next/link'
import { useCartState } from '@/contexts/CartContext'
import CartAside from '@/components/Cart/CartAside'
import CartSteps from '@/components/Cart/CartSteps'
import { CartPageIllustration } from '@/components/Icons/CartPageIllustration'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { track } from "@/lib/tracker";

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? 'en', [
      'products',
          'productsHeroBanner',
          'categoryTabs',
          'filterModal',
          'filterSidebar',
          'desktopHeader',
          'footer',
          'common',
          'account',
          'alerts',
          'address',
          'cart',
          'contactus',
          'deliveryPolicy',
          'notfound',
          'payment',
          'personalizeModal',
          'product',
          'removePhone'
    ])),
  },
})

export default function Cart() {
  const cart = useCartState()
  const { t } = useTranslation('cart')
  const { locale } = useRouter()

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (hasTrackedRef.current) return;

    track({ event_name: "view_cart" });

    hasTrackedRef.current = true;
  }, []);

  return (
    <>
      <Head>
        <title>{t('pageTitle')}</title>
        <meta name="description" content="Basmti Project" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <main className="bg-gray-100 pb-24 pt-4">
        <div className="container-fluid mt-4 cart-page">
          <CartSteps />

          <div
            className={`lg:grid gap-8 ${
              locale === 'ar'
                ? 'grid-cols-[1fr_0.5fr]' // aside first, then items
                : 'grid-cols-[0.5fr_1fr]' // items first, then aside
            }`}
          >
            {/* Cart Items */}
            <div className={`${locale === 'ar' ? 'lg:order-2' : 'lg:order-2'}`}>
              <div
                className="cart-top flex items-center justify-between"
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
              >
                <h1 className="text-center font-semibold">
                  {t('cartItems')} ({cart.items.length})
                </h1>
                <Link
                  href="/products"
                  className="text-xs underline font-light"
                >
                  {t('viewProducts')}
                </Link>
              </div>

              <div className="cart-items mt-1" id='cart-items-container'>
                {cart.items.map((item, index) => (
                  <CartItem
                    key={item.uuid}
                    item={item}
                    index={index}
                    isItemInFloatingCart={false}
                  />
                ))}

                {cart.items.length === 0 && (
                  <div className="cart-empty py-6 flex flex-col justify-center items-center">
                    <h3 className="text-center text-lg text-gray-400">
                      {t('emptyCart')}
                    </h3>
                    <CartPageIllustration />
                  </div>
                )}

                {cart.items.length > 0 && (
                  <div className="lg:max-w-lg mx-auto" id='add-more-products-btn'>
                    <Link href="/products">
                      <Button
                        bg="primary"
                        type="button"
                        classes={[
                          'w-full',
                          'text-center',
                          'py-4',
                          'mt-6',
                          'font-bold',
                        ]}
                      >
                        {`${t('addMore')}`}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Cart Aside */}
            <div className={`${locale === 'ar' ? 'lg:order-2' : 'lg:order-1'}`}>
              <CartAside />
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
